import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Apps as AppsIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  Groups as GroupsIcon,
  Inventory2Outlined as ProductIcon,
  PeopleAltOutlined as UsersIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { baseURL } from "../../Config/config";


/*
Required packages:

npm install @mui/material @emotion/react @emotion/styled
npm install @mui/icons-material @mui/x-data-grid

This component uses the browser fetch API, so axios is not required.
*/

// -----------------------------------------------------------------------------
// API CONFIGURATION
// Change only these endpoint values to match your Flask backend.
// -----------------------------------------------------------------------------

const API_BASE_URL = baseURL
const API_ENDPOINTS = {
  dashboardSummary: "/api/admindesktop/dashboard-summary",

  users: {
    list: "/api/admindesktop/users",
    create: "/api/admindesktop/users",
    update: (id) => `/api/admindesktop/users/${id}`,
    remove: (id) => `/api/admindesktop/users/${id}`,
  },

  products: {
    list: "/api/admindesktop/products",
    create: "/api/admindesktop/products",
    update: (id) => `/api/admindesktop/products/${id}`,
    remove: (id) => `/api/admindesktop/products/${id}`,
  },
};

// -----------------------------------------------------------------------------
// OPTIONS
// -----------------------------------------------------------------------------

const USER_STATUS_OPTIONS = ["Active", "Inactive", "Suspended"];
const USER_ROLE_OPTIONS = ["User", "Admin", "Manager"];

const PRODUCT_STATUS_OPTIONS = ["Active", "Inactive", "Development"];
const PRODUCT_LICENSE_OPTIONS = [
  "Trial",
  "Monthly",
  "Annual",
  "Perpetual",
  "Custom",
];

const EMPTY_USER_FORM = {
  name: "",
  email: "",
  company: "",
  phone: "",
  role: "User",
  status: "Active",
};

const EMPTY_PRODUCT_FORM = {
  name: "",
  productId: "",
  version: "",
  category: "",
  licenseType: "Annual",
  status: "Active",
  description: "",
};

// -----------------------------------------------------------------------------
// API HELPERS
// -----------------------------------------------------------------------------

function getAdminToken() {
  return (
    localStorage.getItem("adminToken") ||
    sessionStorage.getItem("adminToken") ||
    ""
  );
}

async function apiRequest(endpoint, options = {}) {
  const token = getAdminToken();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  const contentType = response.headers.get("content-type") || "";
  let payload = null;

  if (contentType.includes("application/json")) {
    payload = await response.json();
  } else {
    const text = await response.text();
    payload = text ? { message: text } : {};
  }

  if (!response.ok) {
    throw new Error(
      payload?.message ||
        payload?.error ||
        `Request failed with status ${response.status}`
    );
  }

  return payload;
}

function extractArray(payload, possibleKeys = []) {
  if (Array.isArray(payload)) return payload;

  for (const key of possibleKeys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;

  return [];
}

function getRowId(row) {
  return (
    row?.id ||
    row?._id ||
    row?.userId ||
    row?.productId ||
    row?.licenseId ||
    `${Date.now()}-${Math.random()}`
  );
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatUsageHours(row) {
  if (row?.usageHours !== undefined && row?.usageHours !== null) {
    return `${Number(row.usageHours).toFixed(1)} h`;
  }

  if (row?.totalUsageHours !== undefined && row?.totalUsageHours !== null) {
    return `${Number(row.totalUsageHours).toFixed(1)} h`;
  }

  if (
    row?.totalActiveSeconds !== undefined &&
    row?.totalActiveSeconds !== null
  ) {
    return `${(Number(row.totalActiveSeconds) / 3600).toFixed(1)} h`;
  }

  return "0.0 h";
}

function normalizeStatus(status, fallback = "Active") {
  if (!status) return fallback;

  const text = String(status).trim();
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

function statusChipColor(status) {
  const normalized = normalizeStatus(status);

  if (normalized === "Active") return "success";
  if (normalized === "Inactive") return "default";
  if (normalized === "Suspended") return "error";
  if (normalized === "Development") return "warning";

  return "default";
}

// -----------------------------------------------------------------------------
// REUSABLE COMPONENTS
// -----------------------------------------------------------------------------

function SummaryCard({ title, value, subtitle, icon, loading, accent }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "0 auto 0 0",
          width: 5,
          bgcolor: accent,
        }}
      />

      <CardContent sx={{ p: 2.5, pl: 3 }}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={700}
            >
              {title}
            </Typography>

            {loading ? (
              <Box sx={{ mt: 1.5 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Typography
                variant="h4"
                sx={{ mt: 0.5, fontWeight: 800, letterSpacing: "-0.03em" }}
              >
                {value}
              </Typography>
            )}

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              {subtitle}
            </Typography>
          </Box>

          <Avatar
            sx={{
              bgcolor: `${accent}18`,
              color: accent,
              width: 48,
              height: 48,
            }}
          >
            {icon}
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SectionHeader({
  title,
  description,
  actionLabel,
  onAction,
  actionIcon,
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
      spacing={2}
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography variant="h6" fontWeight={800}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>

      <Button
        variant="contained"
        startIcon={actionIcon}
        onClick={onAction}
        sx={{
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 700,
          px: 2.5,
        }}
      >
        {actionLabel}
      </Button>
    </Stack>
  );
}

function FormSelect({ label, value, onChange, options, disabled = false }) {
  return (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={onChange}>
        {options.map((option) => (
          <MenuItem value={option} key={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// -----------------------------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------------------------

export default function AdminDashboardHome() {
  const [activeTab, setActiveTab] = useState(0);

  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState(null);

  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [userSearch, setUserSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: "",
    row: null,
  });

  const [editingUser, setEditingUser] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  const [userForm, setUserForm] = useState(EMPTY_USER_FORM);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT_FORM);

  const [savingUser, setSavingUser] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [userFormErrors, setUserFormErrors] = useState({});
  const [productFormErrors, setProductFormErrors] = useState({});

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showMessage = useCallback((message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  }, []);

  // ---------------------------------------------------------------------------
  // LOAD DATA
  // ---------------------------------------------------------------------------

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);

    try {
      const payload = await apiRequest(API_ENDPOINTS.users.list);
      setUsers(extractArray(payload, ["users"]));
    } catch (error) {
      showMessage(error.message || "Could not load users.", "error");
    } finally {
      setLoadingUsers(false);
    }
  }, [showMessage]);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);

    try {
      const payload = await apiRequest(API_ENDPOINTS.products.list);
      setProducts(extractArray(payload, ["products"]));
    } catch (error) {
      showMessage(error.message || "Could not load products.", "error");
    } finally {
      setLoadingProducts(false);
    }
  }, [showMessage]);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);

    try {
      const payload = await apiRequest(API_ENDPOINTS.dashboardSummary);
      setSummary(payload?.data || payload?.summary || payload);
    } catch (error) {
      // The cards still work from the loaded users/products if this endpoint
      // does not exist yet.
      setSummary(null);
      console.warn("Dashboard summary endpoint failed:", error.message);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const loadAllData = useCallback(async () => {
    setRefreshing(true);

    await Promise.all([loadUsers(), loadProducts(), loadSummary()]);

    setRefreshing(false);
  }, [loadProducts, loadSummary, loadUsers]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // ---------------------------------------------------------------------------
  // DERIVED SUMMARY
  // ---------------------------------------------------------------------------

  const calculatedSummary = useMemo(() => {
    const activeUsers = users.filter(
      (user) => normalizeStatus(user.status) === "Active"
    ).length;

    const activeProducts = products.filter(
      (product) => normalizeStatus(product.status) === "Active"
    ).length;

    const totalUsageSeconds = users.reduce((sum, user) => {
      if (user.totalActiveSeconds !== undefined) {
        return sum + Number(user.totalActiveSeconds || 0);
      }

      const hours =
        user.totalUsageHours !== undefined
          ? user.totalUsageHours
          : user.usageHours;

      return sum + Number(hours || 0) * 3600;
    }, 0);

    const currentlyOnline = users.filter((user) => {
      if (typeof user.isOnline === "boolean") return user.isOnline;
      if (!user.lastSeenAt && !user.lastActive) return false;

      const lastSeen = new Date(user.lastSeenAt || user.lastActive).getTime();
      return Date.now() - lastSeen < 5 * 60 * 1000;
    }).length;

    return {
      totalUsers:
        summary?.totalUsers ??
        summary?.userCount ??
        summary?.users ??
        users.length,

      activeUsers:
        summary?.activeUsers ??
        summary?.activeUserCount ??
        activeUsers,

      totalProducts:
        summary?.totalProducts ??
        summary?.productCount ??
        summary?.products ??
        products.length,

      activeProducts:
        summary?.activeProducts ??
        summary?.activeProductCount ??
        activeProducts,

      totalUsageHours:
        summary?.totalUsageHours ??
        summary?.usageHours ??
        totalUsageSeconds / 3600,

      onlineUsers:
        summary?.onlineUsers ??
        summary?.currentlyOnline ??
        currentlyOnline,
    };
  }, [products, summary, users]);

  // ---------------------------------------------------------------------------
  // FILTERING
  // ---------------------------------------------------------------------------

  const filteredUsers = useMemo(() => {
    const search = userSearch.trim().toLowerCase();

    if (!search) return users;

    return users.filter((user) =>
      [
        user.name,
        user.fullName,
        user.email,
        user.company,
        user.phone,
        user.role,
        user.status,
        user.licenseId,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [userSearch, users]);

  const filteredProducts = useMemo(() => {
    const search = productSearch.trim().toLowerCase();

    if (!search) return products;

    return products.filter((product) =>
      [
        product.name,
        product.productName,
        product.productId,
        product.version,
        product.category,
        product.licenseType,
        product.status,
        product.description,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [productSearch, products]);

  // ---------------------------------------------------------------------------
  // USER CRUD
  // ---------------------------------------------------------------------------

  const openAddUserDialog = () => {
    setEditingUser(null);
    setUserForm(EMPTY_USER_FORM);
    setUserFormErrors({});
    setUserDialogOpen(true);
  };

  const openEditUserDialog = (row) => {
    setEditingUser(row);
    setUserForm({
      name: row.name || row.fullName || "",
      email: row.email || "",
      company: row.company || "",
      phone: row.phone || "",
      role: row.role || "User",
      status: normalizeStatus(row.status),
    });
    setUserFormErrors({});
    setUserDialogOpen(true);
  };

  const validateUserForm = () => {
    const errors = {};

    if (!userForm.name.trim()) {
      errors.name = "Name is required.";
    }

    if (!userForm.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email.trim())) {
      errors.email = "Enter a valid email address.";
    }

    setUserFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveUser = async () => {
    if (!validateUserForm()) return;

    setSavingUser(true);

    try {
      const payload = {
        ...userForm,
        name: userForm.name.trim(),
        email: userForm.email.trim(),
        company: userForm.company.trim(),
        phone: userForm.phone.trim(),
      };

      if (editingUser) {
        const id = getRowId(editingUser);

        await apiRequest(API_ENDPOINTS.users.update(id), {
          method: "PUT",
          body: JSON.stringify(payload),
        });

        showMessage("User updated successfully.");
      } else {
        await apiRequest(API_ENDPOINTS.users.create, {
          method: "POST",
          body: JSON.stringify(payload),
        });

        showMessage("User added successfully.");
      }

      setUserDialogOpen(false);
      await Promise.all([loadUsers(), loadSummary()]);
    } catch (error) {
      showMessage(error.message || "Could not save user.", "error");
    } finally {
      setSavingUser(false);
    }
  };

  // ---------------------------------------------------------------------------
  // PRODUCT CRUD
  // ---------------------------------------------------------------------------

  const openAddProductDialog = () => {
    setEditingProduct(null);
    setProductForm(EMPTY_PRODUCT_FORM);
    setProductFormErrors({});
    setProductDialogOpen(true);
  };

  const openEditProductDialog = (row) => {
    setEditingProduct(row);
    setProductForm({
      name: row.name || row.productName || "",
      productId: row.productId || "",
      version: row.version || "",
      category: row.category || "",
      licenseType: row.licenseType || "Annual",
      status: normalizeStatus(row.status),
      description: row.description || "",
    });
    setProductFormErrors({});
    setProductDialogOpen(true);
  };

  const validateProductForm = () => {
    const errors = {};

    if (!productForm.name.trim()) {
      errors.name = "Product name is required.";
    }

    if (!productForm.productId.trim()) {
      errors.productId = "Product ID is required.";
    }

    if (!productForm.version.trim()) {
      errors.version = "Version is required.";
    }

    setProductFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveProduct = async () => {
    if (!validateProductForm()) return;

    setSavingProduct(true);

    try {
      const payload = {
        ...productForm,
        name: productForm.name.trim(),
        productId: productForm.productId.trim(),
        version: productForm.version.trim(),
        category: productForm.category.trim(),
        description: productForm.description.trim(),
      };

      if (editingProduct) {
        const id = getRowId(editingProduct);

        await apiRequest(API_ENDPOINTS.products.update(id), {
          method: "PUT",
          body: JSON.stringify(payload),
        });

        showMessage("Product updated successfully.");
      } else {
        await apiRequest(API_ENDPOINTS.products.create, {
          method: "POST",
          body: JSON.stringify(payload),
        });

        showMessage("Product added successfully.");
      }

      setProductDialogOpen(false);
      await Promise.all([loadProducts(), loadSummary()]);
    } catch (error) {
      showMessage(error.message || "Could not save product.", "error");
    } finally {
      setSavingProduct(false);
    }
  };

  // ---------------------------------------------------------------------------
  // DELETE
  // ---------------------------------------------------------------------------

  const askToDelete = (type, row) => {
    setDeleteDialog({
      open: true,
      type,
      row,
    });
  };

  const closeDeleteDialog = () => {
    if (deleting) return;

    setDeleteDialog({
      open: false,
      type: "",
      row: null,
    });
  };

  const confirmDelete = async () => {
    const { type, row } = deleteDialog;
    if (!row || !type) return;

    setDeleting(true);

    try {
      const id = getRowId(row);
      const endpoint =
        type === "user"
          ? API_ENDPOINTS.users.remove(id)
          : API_ENDPOINTS.products.remove(id);

      await apiRequest(endpoint, {
        method: "DELETE",
      });

      showMessage(
        type === "user"
          ? "User deleted successfully."
          : "Product deleted successfully."
      );

      closeDeleteDialog();

      if (type === "user") {
        await Promise.all([loadUsers(), loadSummary()]);
      } else {
        await Promise.all([loadProducts(), loadSummary()]);
      }
    } catch (error) {
      showMessage(error.message || "Could not delete item.", "error");
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // DATA GRID COLUMNS
  // ---------------------------------------------------------------------------

  const userColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "User",
        minWidth: 220,
        flex: 1.2,
        sortable: true,
        renderCell: (params) => {
          const name =
            params.row.name || params.row.fullName || "Unnamed User";
          const email = params.row.email || "No email";

          return (
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  fontSize: 14,
                  fontWeight: 800,
                  bgcolor: "primary.main",
                }}
              >
                {name
                  .split(" ")
                  .slice(0, 2)
                  .map((part) => part[0])
                  .join("")
                  .toUpperCase()}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  noWrap
                  title={name}
                >
                  {name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={email}
                  sx={{ display: "block" }}
                >
                  {email}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
      {
        field: "company",
        headerName: "Company",
        minWidth: 150,
        flex: 0.9,
        valueGetter: (value, row) => row.company || "—",
      },
      {
        field: "role",
        headerName: "Role",
        minWidth: 110,
        flex: 0.6,
        valueGetter: (value, row) => row.role || "User",
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 120,
        flex: 0.65,
        renderCell: (params) => (
          <Chip
            label={normalizeStatus(params.row.status)}
            color={statusChipColor(params.row.status)}
            size="small"
            variant={
              normalizeStatus(params.row.status) === "Inactive"
                ? "outlined"
                : "filled"
            }
            sx={{ fontWeight: 700 }}
          />
        ),
      },
      {
        field: "usage",
        headerName: "Usage",
        minWidth: 105,
        flex: 0.55,
        sortable: false,
        valueGetter: (value, row) => formatUsageHours(row),
      },
      {
        field: "lastSeenAt",
        headerName: "Last Active",
        minWidth: 175,
        flex: 0.9,
        valueGetter: (value, row) =>
          formatDate(row.lastSeenAt || row.lastActive),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Edit user">
              <IconButton
                size="small"
                color="primary"
                onClick={(event) => {
                  event.stopPropagation();
                  openEditUserDialog(params.row);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete user">
              <IconButton
                size="small"
                color="error"
                onClick={(event) => {
                  event.stopPropagation();
                  askToDelete("user", params.row);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    []
  );

  const productColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Product",
        minWidth: 220,
        flex: 1.2,
        renderCell: (params) => {
          const name =
            params.row.name || params.row.productName || "Unnamed Product";

          return (
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar
                variant="rounded"
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "secondary.main",
                }}
              >
                <AppsIcon fontSize="small" />
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: "block" }}
                >
                  {params.row.productId || "No product ID"}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
      {
        field: "version",
        headerName: "Version",
        minWidth: 100,
        flex: 0.5,
        valueGetter: (value, row) => row.version || "—",
      },
      {
        field: "category",
        headerName: "Category",
        minWidth: 140,
        flex: 0.75,
        valueGetter: (value, row) => row.category || "—",
      },
      {
        field: "licenseType",
        headerName: "License",
        minWidth: 120,
        flex: 0.65,
        valueGetter: (value, row) => row.licenseType || "—",
      },
      {
        field: "users",
        headerName: "Users",
        minWidth: 90,
        flex: 0.45,
        type: "number",
        valueGetter: (value, row) =>
          Number(row.userCount ?? row.totalUsers ?? row.users ?? 0),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 125,
        flex: 0.65,
        renderCell: (params) => (
          <Chip
            label={normalizeStatus(params.row.status)}
            color={statusChipColor(params.row.status)}
            size="small"
            sx={{ fontWeight: 700 }}
          />
        ),
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        minWidth: 170,
        flex: 0.85,
        valueGetter: (value, row) =>
          formatDate(row.updatedAt || row.createdAt),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Edit product">
              <IconButton
                size="small"
                color="primary"
                onClick={(event) => {
                  event.stopPropagation();
                  openEditProductDialog(params.row);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete product">
              <IconButton
                size="small"
                color="error"
                onClick={(event) => {
                  event.stopPropagation();
                  askToDelete("product", params.row);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    []
  );

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f6f8fc" }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,0.94)",
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
          backdropFilter: "blur(12px)",
        }}
      >
        <Toolbar sx={{ minHeight: 68 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: "primary.main",
                width: 42,
                height: 42,
                fontWeight: 900,
              }}
            >
              S
            </Avatar>

            <Box>
              <Typography variant="h6" fontWeight={900} lineHeight={1.1}>
                SelexSuite Admin
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Users, products and platform activity
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Refresh all dashboard data">
            <span>
              <IconButton
                onClick={loadAllData}
                disabled={refreshing}
                color="primary"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1.5, my: 1.5 }} />

          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ width: 36, height: 36, bgcolor: "secondary.main" }}>
              A
            </Avatar>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography variant="body2" fontWeight={800}>
                Administrator
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Admin access
              </Typography>
            </Box>
          </Stack>
        </Toolbar>

        {refreshing && <LinearProgress />}
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3.5 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 900, letterSpacing: "-0.035em" }}
            >
              Dashboard Overview
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage desktop application users and products from one place.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openAddProductDialog}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              Add Product
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddUserDialog}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
              }}
            >
              Add User
            </Button>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
            mb: 3,
          }}
        >
          <SummaryCard
            title="Total Users"
            value={calculatedSummary.totalUsers}
            subtitle={`${calculatedSummary.activeUsers} active accounts`}
            icon={<UsersIcon />}
            loading={loadingUsers && loadingSummary}
            accent="#1976d2"
          />

          <SummaryCard
            title="Products"
            value={calculatedSummary.totalProducts}
            subtitle={`${calculatedSummary.activeProducts} active products`}
            icon={<ProductIcon />}
            loading={loadingProducts && loadingSummary}
            accent="#7b1fa2"
          />

          <SummaryCard
            title="Total Usage"
            value={`${Number(calculatedSummary.totalUsageHours || 0).toFixed(
              1
            )} h`}
            subtitle="Combined active application time"
            icon={<ScheduleIcon />}
            loading={loadingSummary}
            accent="#ed6c02"
          />

          <SummaryCard
            title="Online Now"
            value={calculatedSummary.onlineUsers}
            subtitle="Seen within the last 5 minutes"
            icon={<CheckCircleIcon />}
            loading={loadingSummary}
            accent="#2e7d32"
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2.5, pt: 1 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label="Admin dashboard sections"
            >
              <Tab
                icon={<GroupsIcon fontSize="small" />}
                iconPosition="start"
                label={`Users (${users.length})`}
                sx={{ textTransform: "none", fontWeight: 800 }}
              />
              <Tab
                icon={<AppsIcon fontSize="small" />}
                iconPosition="start"
                label={`Products (${products.length})`}
                sx={{ textTransform: "none", fontWeight: 800 }}
              />
            </Tabs>
          </Box>

          <Divider />

          {activeTab === 0 && (
            <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              <SectionHeader
                title="User Management"
                description="Add, update, search and remove customer or admin accounts."
                actionLabel="Add User"
                onAction={openAddUserDialog}
                actionIcon={<AddIcon />}
              />

              <TextField
                fullWidth
                size="small"
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
                placeholder="Search by name, email, company, role, status or licence ID"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: userSearch ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setUserSearch("")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <Box sx={{ width: "100%", height: 570 }}>
                <DataGrid
                  rows={filteredUsers}
                  columns={userColumns}
                  getRowId={getRowId}
                  loading={loadingUsers}
                  disableRowSelectionOnClick
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                        page: 0,
                      },
                    },
                  }}
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: false,
                      printOptions: { disableToolbarButton: true },
                    },
                  }}
                  sx={{
                    border: 0,
                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor: "#f8fafc",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 800,
                    },
                    "& .MuiDataGrid-cell": {
                      borderColor: "divider",
                    },
                    "& .MuiDataGrid-row:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                />
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              <SectionHeader
                title="Product Management"
                description="Register and manage every Electron desktop product."
                actionLabel="Add Product"
                onAction={openAddProductDialog}
                actionIcon={<AddIcon />}
              />

              <TextField
                fullWidth
                size="small"
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
                placeholder="Search by product name, ID, version, category or status"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: productSearch ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setProductSearch("")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <Box sx={{ width: "100%", height: 570 }}>
                <DataGrid
                  rows={filteredProducts}
                  columns={productColumns}
                  getRowId={getRowId}
                  loading={loadingProducts}
                  disableRowSelectionOnClick
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                        page: 0,
                      },
                    },
                  }}
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: false,
                      printOptions: { disableToolbarButton: true },
                    },
                  }}
                  sx={{
                    border: 0,
                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor: "#f8fafc",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 800,
                    },
                    "& .MuiDataGrid-cell": {
                      borderColor: "divider",
                    },
                    "& .MuiDataGrid-row:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </Paper>
      </Container>

      {/* USER ADD / EDIT DIALOG */}
      <Dialog
        open={userDialogOpen}
        onClose={() => !savingUser && setUserDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              <UsersIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                {editingUser ? "Edit User" : "Add New User"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editingUser
                  ? "Update the selected user account."
                  : "Create a new user account."}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              pt: 1,
            }}
          >
            <TextField
              label="Full Name"
              fullWidth
              required
              value={userForm.name}
              onChange={(event) =>
                setUserForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              error={Boolean(userFormErrors.name)}
              helperText={userFormErrors.name}
            />

            <TextField
              label="Email Address"
              type="email"
              fullWidth
              required
              value={userForm.email}
              onChange={(event) =>
                setUserForm((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
              error={Boolean(userFormErrors.email)}
              helperText={userFormErrors.email}
            />

            <TextField
              label="Company"
              fullWidth
              value={userForm.company}
              onChange={(event) =>
                setUserForm((current) => ({
                  ...current,
                  company: event.target.value,
                }))
              }
            />

            <TextField
              label="Phone Number"
              fullWidth
              value={userForm.phone}
              onChange={(event) =>
                setUserForm((current) => ({
                  ...current,
                  phone: event.target.value,
                }))
              }
            />

            <FormSelect
              label="Role"
              value={userForm.role}
              options={USER_ROLE_OPTIONS}
              onChange={(event) =>
                setUserForm((current) => ({
                  ...current,
                  role: event.target.value,
                }))
              }
            />

            <FormSelect
              label="Status"
              value={userForm.status}
              options={USER_STATUS_OPTIONS}
              onChange={(event) =>
                setUserForm((current) => ({
                  ...current,
                  status: event.target.value,
                }))
              }
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setUserDialogOpen(false)}
            disabled={savingUser}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={saveUser}
            disabled={savingUser}
            startIcon={
              savingUser ? <CircularProgress size={18} color="inherit" /> : null
            }
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
          >
            {savingUser
              ? "Saving..."
              : editingUser
              ? "Update User"
              : "Add User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PRODUCT ADD / EDIT DIALOG */}
      <Dialog
        open={productDialogOpen}
        onClose={() => !savingProduct && setProductDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ bgcolor: "secondary.main" }}>
              <ProductIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                {editingProduct ? "Edit Product" : "Add New Product"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Register the product details used by your Electron application.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              pt: 1,
            }}
          >
            <TextField
              label="Product Name"
              fullWidth
              required
              value={productForm.name}
              onChange={(event) =>
                setProductForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              error={Boolean(productFormErrors.name)}
              helperText={productFormErrors.name}
            />

            <TextField
              label="Product ID"
              fullWidth
              required
              placeholder="Example: selexrxnv2.0XP"
              value={productForm.productId}
              onChange={(event) =>
                setProductForm((current) => ({
                  ...current,
                  productId: event.target.value,
                }))
              }
              error={Boolean(productFormErrors.productId)}
              helperText={
                productFormErrors.productId ||
                "This must match the productId inside the Electron licence."
              }
              disabled={Boolean(editingProduct)}
            />

            <TextField
              label="Version"
              fullWidth
              required
              placeholder="Example: 2.0.0"
              value={productForm.version}
              onChange={(event) =>
                setProductForm((current) => ({
                  ...current,
                  version: event.target.value,
                }))
              }
              error={Boolean(productFormErrors.version)}
              helperText={productFormErrors.version}
            />

            <TextField
              label="Category"
              fullWidth
              placeholder="Example: Simulation"
              value={productForm.category}
              onChange={(event) =>
                setProductForm((current) => ({
                  ...current,
                  category: event.target.value,
                }))
              }
            />

            <FormSelect
              label="License Type"
              value={productForm.licenseType}
              options={PRODUCT_LICENSE_OPTIONS}
              onChange={(event) =>
                setProductForm((current) => ({
                  ...current,
                  licenseType: event.target.value,
                }))
              }
            />

            <FormSelect
              label="Status"
              value={productForm.status}
              options={PRODUCT_STATUS_OPTIONS}
              onChange={(event) =>
                setProductForm((current) => ({
                  ...current,
                  status: event.target.value,
                }))
              }
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={3}
              value={productForm.description}
              onChange={(event) =>
                setProductForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setProductDialogOpen(false)}
            disabled={savingProduct}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={saveProduct}
            disabled={savingProduct}
            startIcon={
              savingProduct ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
          >
            {savingProduct
              ? "Saving..."
              : editingProduct
              ? "Update Product"
              : "Add Product"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar sx={{ bgcolor: "error.light", color: "error.dark" }}>
              <WarningIcon />
            </Avatar>
            <Typography variant="h6" fontWeight={800}>
              Confirm Delete
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>
              {deleteDialog.type === "user"
                ? deleteDialog.row?.name ||
                  deleteDialog.row?.fullName ||
                  deleteDialog.row?.email
                : deleteDialog.row?.name ||
                  deleteDialog.row?.productName ||
                  deleteDialog.row?.productId}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={closeDeleteDialog}
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SUCCESS / ERROR MESSAGE */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() =>
          setSnackbar((current) => ({
            ...current,
            open: false,
          }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((current) => ({
              ...current,
              open: false,
            }))
          }
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
